import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { adminApi, API } from "../../lib/api";
import { Utensils, Plus, ChevronLeft, ChevronRight, X, Copy, Save, Check } from "lucide-react";
import { toast } from "sonner";

const SLOT_DEFS = [
  { id: "mid_morning", label: "Mid-morning snack", short: "Mid-morning snack" },
  { id: "lunch", label: "Lunch", short: "Lunch" },
  { id: "evening", label: "Evening snack", short: "Evening snack" },
];
const DAY_DEFS = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const mondayOf = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); // 0 Sun..6 Sat
  const diff = (dow + 6) % 7; // days since Monday
  dt.setDate(dt.getDate() - diff);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
const friendlyDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};
const weekRangeLabel = (mondayIso) => {
  const friIso = addDays(mondayIso, 4);
  const [, fm] = friendlyDate(friIso).split(" ");
  return `${friendlyDate(mondayIso)} — ${friendlyDate(friIso)} ${(new Date(mondayIso)).getFullYear()}`;
};

const emptyItems = () => Object.fromEntries(SLOT_DEFS.map((s) => [s.id, Object.fromEntries(DAY_DEFS.map((d) => [d.id, []]))]));

export default function MenuPanel({ can }) {
  const editable = can("manage_menu");
  const [weekOf, setWeekOf] = useState(mondayOf(todayIso()));
  const [slots, setSlots] = useState(["mid_morning", "lunch"]);
  const [items, setItems] = useState(emptyItems());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [editCell, setEditCell] = useState(null); // { slot, day }

  const load = async (target = weekOf) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/food-menu/week`, { params: { week_of: target } });
      setWeekOf(data.week_of);
      setSlots(data.slots && data.slots.length ? data.slots : ["mid_morning", "lunch"]);
      setItems(mergeItems(data.items));
      setUpdatedAt(data.updated_at || "");
      setDirty(false);
    } catch {
      toast.error("Failed to load menu");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(weekOf); /* eslint-disable-next-line */ }, []);

  const goWeek = (n) => {
    const next = addDays(weekOf, n * 7);
    setWeekOf(next);
    load(next);
  };

  const toggleSlot = (id) => {
    if (!editable) return;
    setSlots((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
    setDirty(true);
  };

  const updateCell = (slot, day, list) => {
    setItems((prev) => ({ ...prev, [slot]: { ...prev[slot], [day]: list } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const orderedSlots = SLOT_DEFS.filter((s) => slots.includes(s.id)).map((s) => s.id);
      const { data } = await adminApi.post("/admin/food-menu/week", { week_of: weekOf, slots: orderedSlots, items });
      setUpdatedAt(data.updated_at || "");
      setDirty(false);
      toast.success("Menu published. Parents will see it on their portal.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const duplicateLastWeek = async () => {
    try {
      const { data } = await adminApi.get("/admin/food-menu/last-week", { params: { before: weekOf } });
      if (!data) return toast("No earlier week to copy from.");
      setSlots(data.slots && data.slots.length ? data.slots : ["mid_morning", "lunch"]);
      setItems(mergeItems(data.items));
      setDirty(true);
      toast.success(`Copied items from week of ${friendlyDate(data.week_of)}. Review and Save to publish.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Copy failed");
    }
  };

  const orderedSlots = useMemo(() => SLOT_DEFS.filter((s) => slots.includes(s.id)), [slots]);

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="weekly-menu-panel">
      {/* Header */}
      <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#5BC4B8]/20 text-[#0F1B47] flex items-center justify-center">
            <Utensils size={18} />
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#5BC4B8]">Weekly Menu</div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium mt-0.5">Weekly Food Menu</h2>
            <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Pick which meals you serve, then enter each day's items as a list. Parents see the same on their portal — no PDF needed.</p>
          </div>
        </div>
        {editable && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={duplicateLastWeek}
              className="inline-flex items-center gap-2 bg-[#0F1B47]/5 text-[#0F1B47] rounded-full px-4 py-2.5 font-semibold text-sm hover:bg-[#0F1B47]/10"
              data-testid="menu-duplicate-btn"
            >
              <Copy size={13} /> Duplicate last week
            </button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
              data-testid="menu-publish-btn"
            >
              {dirty ? <Save size={13} /> : <Check size={13} />} {saving ? "Saving…" : dirty ? "Publish menu" : "Up to date"}
            </button>
          </div>
        )}
      </div>

      {/* Slot picker */}
      {editable && (
        <div className="px-6 md:px-7 pt-5">
          <div className="bg-[#F5F5F0] rounded-2xl p-4">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#4A5568] mb-2.5">Meal slots — pick what you want to publish</div>
            <div className="flex flex-wrap gap-2">
              {SLOT_DEFS.map((s) => {
                const active = slots.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleSlot(s.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold border-2 transition-colors ${active ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]" : "bg-[#FDFDF9] text-[#4A5568] border-[#0F1B47]/12"}`}
                    data-testid={`menu-slot-toggle-${s.id}`}
                  >
                    <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] font-black ${active ? "bg-[#C6D947] text-[#0F1B47]" : "bg-white border border-[#0F1B47]/20 text-transparent"}`}>✓</span>
                    {s.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#718096] mt-2">Only the checked slots show on the grid below and on the parent portal. You can change this anytime.</p>
          </div>
        </div>
      )}

      {/* Week navigator */}
      <div className="px-6 md:px-7 pt-5">
        <div className="flex items-center justify-between bg-[#F5F5F0] rounded-2xl p-3" data-testid="menu-week-nav">
          <button onClick={() => goWeek(-1)} className="p-2 rounded-full hover:bg-[#0F1B47]/10 text-[#0F1B47]" data-testid="menu-prev-week"><ChevronLeft size={16} /></button>
          <div className="flex items-center gap-3">
            <div className="font-display text-lg text-[#0F1B47] font-medium" data-testid="menu-week-label">{weekRangeLabel(weekOf)}</div>
            <input
              type="date"
              value={weekOf}
              onChange={(e) => { const m = mondayOf(e.target.value); setWeekOf(m); load(m); }}
              className="text-[12px] bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-full px-3 py-1 text-[#0F1B47]"
              data-testid="menu-week-date"
            />
            <button onClick={() => { const m = mondayOf(todayIso()); setWeekOf(m); load(m); }} className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] px-3 py-1 rounded-full bg-[#FDFDF9] hover:bg-[#0F1B47]/5" data-testid="menu-this-week">This week</button>
          </div>
          <button onClick={() => goWeek(1)} className="p-2 rounded-full hover:bg-[#0F1B47]/10 text-[#0F1B47]" data-testid="menu-next-week"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Grid */}
      <div className="p-6 md:p-7">
        {loading ? (
          <div className="text-sm text-[#718096] py-10 text-center">Loading menu…</div>
        ) : orderedSlots.length === 0 ? (
          <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
            <Utensils size={26} className="mx-auto text-[#5BC4B8] mb-2" />
            <div className="text-[#0F1B47] font-medium">No meal slots selected</div>
            <p className="text-xs text-[#718096] mt-1">Toggle at least one meal slot above to start adding items.</p>
          </div>
        ) : (
          <div className="grid gap-2" style={{ gridTemplateColumns: "140px repeat(5, minmax(0, 1fr))" }}>
            <div />
            {DAY_DEFS.map((d, i) => (
              <div key={d.id} className="text-center text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] py-2">
                {d.label} · {(new Date(addDays(weekOf, i))).getDate()}
              </div>
            ))}
            {orderedSlots.map((slot) => (
              <React.Fragment key={slot.id}>
                <div className="bg-[#F5F5F0] rounded-2xl flex items-center justify-center text-center text-[11px] font-bold tracking-[0.16em] uppercase text-[#4A5568] p-3">
                  {slot.short}
                </div>
                {DAY_DEFS.map((d) => {
                  const list = items[slot.id]?.[d.id] || [];
                  return (
                    <button
                      key={d.id}
                      onClick={() => editable && setEditCell({ slot: slot.id, day: d.id })}
                      className={`text-left bg-[#FDFDF9] border border-[#0F1B47]/10 rounded-2xl p-3 min-h-[96px] relative ${editable ? "hover:border-[#648DE5] hover:shadow-md cursor-pointer" : ""} transition-all`}
                      data-testid={`menu-cell-${slot.id}-${d.id}`}
                    >
                      {list.length === 0 ? (
                        <div className="text-[11px] text-[#A0AEC0] italic">{editable ? "Click to add" : "—"}</div>
                      ) : (
                        <ul className="list-disc pl-4 text-[12px] leading-[1.5] text-[#0F1B47] space-y-0.5">
                          {list.map((it, i) => <li key={i}>{it}</li>)}
                        </ul>
                      )}
                      {editable && (
                        <span className="absolute right-2 bottom-2 w-5 h-5 rounded-full bg-[#0F1B47]/5 flex items-center justify-center text-[#0F1B47] text-[12px] font-semibold">+</span>
                      )}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}

        {updatedAt && (
          <div className="mt-3 text-[11px] text-[#A0AEC0]">Last published {new Date(updatedAt).toLocaleString()}</div>
        )}
      </div>

      {editCell && (
        <CellEditor
          slotLabel={SLOT_DEFS.find((s) => s.id === editCell.slot)?.label || ""}
          dayLabel={DAY_DEFS.find((d) => d.id === editCell.day)?.label || ""}
          dayDate={(() => {
            const idx = DAY_DEFS.findIndex((d) => d.id === editCell.day);
            return friendlyDate(addDays(weekOf, idx));
          })()}
          initial={items[editCell.slot]?.[editCell.day] || []}
          onClose={() => setEditCell(null)}
          onSave={(list) => { updateCell(editCell.slot, editCell.day, list); setEditCell(null); }}
        />
      )}
    </div>
  );
}

function mergeItems(payload) {
  const out = emptyItems();
  if (!payload || typeof payload !== "object") return out;
  for (const s of SLOT_DEFS) {
    const sd = payload[s.id] || {};
    for (const d of DAY_DEFS) {
      const raw = Array.isArray(sd[d.id]) ? sd[d.id] : [];
      out[s.id][d.id] = raw.map((x) => String(x || "").trim()).filter(Boolean).slice(0, 12);
    }
  }
  return out;
}

function CellEditor({ slotLabel, dayLabel, dayDate, initial, onClose, onSave }) {
  const [list, setList] = useState(() => (initial.length ? [...initial, ""] : [""]));

  const update = (i, v) => {
    setList((prev) => {
      const next = [...prev];
      next[i] = v;
      // Ensure there's always one empty input at the end
      const cleaned = next.filter((x, idx) => idx < next.length - 1 || x.trim() !== "");
      if (cleaned.length === 0 || cleaned[cleaned.length - 1].trim() !== "") cleaned.push("");
      return cleaned.slice(0, 13);
    });
  };
  const remove = (i) => setList((prev) => {
    const next = prev.filter((_, j) => j !== i);
    if (next.length === 0 || next[next.length - 1].trim() !== "") next.push("");
    return next;
  });
  const submit = (e) => {
    e.preventDefault();
    const cleaned = list.map((x) => x.trim()).filter(Boolean);
    onSave(cleaned);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#FDFDF9] rounded-3xl w-full max-w-md shadow-2xl" data-testid="menu-cell-editor">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Edit</div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">{dayLabel} · {slotLabel}</h3>
            <p className="text-xs text-[#4A5568] mt-0.5">{dayDate} · add one item per line</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-2 max-h-[60vh] overflow-y-auto">
          {list.map((value, i) => {
            const isLast = i === list.length - 1;
            return (
              <div key={i} className="flex items-center gap-2 bg-[#F5F5F0] rounded-2xl px-3 py-1.5">
                <input
                  value={value}
                  onChange={(e) => update(i, e.target.value)}
                  placeholder={isLast ? "+ Add another item" : "Item"}
                  className="flex-1 bg-transparent text-[13px] text-[#0F1B47] focus:outline-none placeholder:text-[#A0AEC0]"
                  maxLength={140}
                  autoFocus={isLast}
                  data-testid={`menu-cell-item-${i}`}
                />
                {!isLast && (
                  <button type="button" onClick={() => remove(i)} className="text-red-600 hover:text-red-700 px-2 py-1 text-sm font-bold">✕</button>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3">
          <button type="submit" className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]" data-testid="menu-cell-save">
            Save
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}
